import React, { useState, useEffect } from 'react';
import EditableText from './EditableText';
import EditableTable from './EditableTable';
import { useEditMode } from '../contexts/EditModeContext';
import { GenericButton, GenericButtonEditor } from './GenericButton';

const AVAILABLE_FLOWERS = [
  'IMG-20251023-WA0003.png',
  'IMG-20251023-WA0006.png',
  'IMG-20251023-WA0007.png',
  'IMG-20251023-WA0008.png',
  'IMG-20251023-WA0009.png',
  'IMG-20251023-WA0011.png',
  'IMG-20251023-WA0012.png',
  'IMG-20251023-WA0014.png',
];


function GenericButtonSectionItem({ item, onChange, isEditMode }) {
  const [showConfig, setShowConfig] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginBottom: '1rem' }}>
      <GenericButton
        item={item}
        isEditMode={isEditMode}
        onClick={isEditMode ? (e => { e.preventDefault(); setShowConfig(!showConfig); }) : undefined}
      />
      {isEditMode && showConfig && (
        <GenericButtonEditor
          item={item}
          onChange={onChange}
          onClose={() => setShowConfig(false)}
        />
      )}
      {isEditMode && !showConfig && (
        <button
          type="button"
          onClick={() => setShowConfig(true)}
          className="btn-small"
          style={{ fontSize: '0.75rem', marginTop: '0.25rem', background: '#d4a574' }}
        >
          ⚙️ Edit Button Settings
        </button>
      )}
    </div>
  );
}

function GenericSection({ section, onChange }) {
  const { isEditMode } = useEditMode();
  const [showFlowerPicker, setShowFlowerPicker] = useState(false);
  const [activeTextColorPicker, setActiveTextColorPicker] = useState(null);

  useEffect(() => {
    const handleOutsideClick = () => {
      if (activeTextColorPicker !== null) {
        setActiveTextColorPicker(null);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [activeTextColorPicker]);

  const handleContentChange = (index, field, value) => {
    const updated = [...section.content];
    if (field === '') {
      updated[index] = value;
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    onChange({ ...section, content: updated });
  };

  const addContent = (type) => {
    let newItem;
    if (type === 'text') {
      newItem = { type: 'text', value: 'New text content' };
    } else if (type === 'image') {
      newItem = { type: 'image', url: '', alt: '' };
    } else if (type === 'table') {
      newItem = {
        type: 'table',
        columnWidths: ['50%', '50%'],
        rowHeights: ['auto', 'auto'],
        rows: [
          {
            cells: [
              { type: 'text', value: 'Cell 1 text', colSpan: 1, rowSpan: 1, isHidden: false },
              { type: 'text', value: 'Cell 2 text', colSpan: 1, rowSpan: 1, isHidden: false }
            ]
          },
          {
            cells: [
              { type: 'text', value: 'Cell 3 text', colSpan: 1, rowSpan: 1, isHidden: false },
              { type: 'text', value: 'Cell 4 text', colSpan: 1, rowSpan: 1, isHidden: false }
            ]
          }
        ]
      };
    } else if (type === 'button') {
      newItem = {
        type: 'button',
        text: 'Click Here',
        url: '#',
        icon: '',
        pack: 'default',
        color: 'var(--color-primary)',
        textColor: '#ffffff',
        styleType: 'solid',
        align: 'center',
        openInNewTab: false
      };
    }
    onChange({ ...section, content: [...section.content, newItem] });
  };

  const removeContent = (index) => {
    const updated = section.content.filter((_, i) => i !== index);
    onChange({ ...section, content: updated });
  };

  const moveContent = (index, direction) => {
    const updated = [...section.content];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= updated.length) return;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange({ ...section, content: updated });
  };

  const handleImageUpload = async (index, file) => {
    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await fetch('/api/upload-image', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      const imageUrl = data.url || data.image?.url;
      if (imageUrl) {
        handleContentChange(index, 'url', imageUrl);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const setFlower = (position, flower) => {
    onChange({ ...section, flower: { ...section.flower, [position]: flower } });
    setShowFlowerPicker(false);
  };

  const removeFlower = (position) => {
    const updated = { ...section.flower };
    delete updated[position];
    onChange({ ...section, flower: updated });
  };

  return (
    <div style={{ position: 'relative' }}>
      {section.flower?.left && (
        <img 
          src={`/flowers/${section.flower.left}`} 
          alt="" 
          className="section-flower section-flower-left" 
          loading="lazy" 
        />
      )}
      {section.flower?.right && (
        <img 
          src={`/flowers/${section.flower.right}`} 
          alt="" 
          className="section-flower section-flower-right" 
          loading="lazy" 
        />
      )}
      
      <section className="section" id={section.id}>
        <EditableText
          value={section.title}
          onChange={value => onChange({ ...section, title: value })}
          className="section-title"
          tag="h2"
        />
        
        {isEditMode && (
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1rem' }}>
            <button onClick={() => setShowFlowerPicker('left')} className="btn-small">
              {section.flower?.left ? '✏️ Left Flower' : '+ Left Flower'}
            </button>
            <button onClick={() => setShowFlowerPicker('right')} className="btn-small">
              {section.flower?.right ? '✏️ Right Flower' : '+ Right Flower'}
            </button>
            {section.flower?.left && (
              <button onClick={() => removeFlower('left')} className="btn-small" style={{ background: '#c77c7c' }}>
                × Left
              </button>
            )}
            {section.flower?.right && (
              <button onClick={() => removeFlower('right')} className="btn-small" style={{ background: '#c77c7c' }}>
                × Right
              </button>
            )}
          </div>
        )}

        {showFlowerPicker && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            zIndex: 2000,
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Select {showFlowerPicker} flower</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
              {AVAILABLE_FLOWERS.map(flower => (
                <img
                  key={flower}
                  src={`/flowers/${flower}`}
                  alt=""
                  onClick={() => setFlower(showFlowerPicker, flower)}
                  style={{
                    width: '100%',
                    cursor: 'pointer',
                    border: section.flower?.[showFlowerPicker] === flower ? '3px solid var(--color-primary)' : '2px solid #ddd',
                    borderRadius: '4px',
                    padding: '0.5rem'
                  }}
                />
              ))}
            </div>
            <button onClick={() => setShowFlowerPicker(false)} className="btn" style={{ marginTop: '1rem' }}>
              Close
            </button>
          </div>
        )}

        {showFlowerPicker && (
          <div 
            onClick={() => setShowFlowerPicker(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1999
            }}
          />
        )}
        
        {section.content.map((item, index) => (
          <div key={index} style={{ position: 'relative', marginBottom: '2rem' }}>
            {item.type === 'table' ? (
              <EditableTable
                table={item}
                onChange={value => handleContentChange(index, '', value)}
              />
            ) : item.type === 'text' ? (
              <EditableText
                value={item.value}
                onChange={value => handleContentChange(index, 'value', value)}
                className="section-text"
                tag="p"
                html={true}
              />
            ) : item.type === 'button' ? (
              <GenericButtonSectionItem
                item={item}
                onChange={value => handleContentChange(index, '', value)}
                isEditMode={isEditMode}
              />
            ) : (
              <div style={{ textAlign: 'center' }}>
                {item.url ? (
                  <img 
                    src={item.url} 
                    alt={item.alt || ''} 
                    style={{ maxWidth: '800px', width: '100%', borderRadius: '8px' }}
                  />
                ) : (
                  isEditMode && (
                    <div style={{ padding: '2rem', border: '2px dashed #ccc', borderRadius: '8px' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => e.target.files[0] && handleImageUpload(index, e.target.files[0])}
                      />
                    </div>
                  )
                )}
                {isEditMode && item.url && (
                  <EditableText
                    value={item.alt || ''}
                    onChange={value => handleContentChange(index, 'alt', value)}
                    className="section-text"
                    tag="p"
                    style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}
                  />
                )}
              </div>
            )}
            
            {isEditMode && (
              <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => moveContent(index, 'up')} disabled={index === 0} className="btn-small">↑</button>
                <button onClick={() => moveContent(index, 'down')} disabled={index === section.content.length - 1} className="btn-small">↓</button>
                <button onClick={() => removeContent(index)} className="btn-small" style={{ background: '#c77c7c' }}>×</button>
              </div>
            )}
          </div>
        ))}

        {isEditMode && (
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
            <button onClick={() => addContent('text')} className="btn">Add Text</button>
            <button onClick={() => addContent('image')} className="btn">Add Image</button>
            <button onClick={() => addContent('table')} className="btn">Add Table</button>
            <button onClick={() => addContent('button')} className="btn">Add Button</button>
          </div>
        )}
      </section>
    </div>
  );
}

export default GenericSection;
